import { useTranslation } from "react-i18next";
import type {
  EmployerApplicantCvSnapshot,
  EmployerApplicantDetail,
} from "@tabbio/types";

/**
 * Web twin of `EmployerApplicantCvPaper.tsx` — the canonical employer-facing
 * CV paper (PRD §5.6). Extracted from the inline copy that used to live in
 * `EmployerApplicantDetailScreen.web.tsx` so the per-job detail and the
 * company-wide Applications inbox render the same document.
 *
 * The paper is labeled "Submitted CV" with the submitted/updated time; a
 * payload marked `snapshot: false` (live-CV fallback, talent submissions) is
 * labeled "Current CV" instead so the employer never mistakes a live document
 * for the immutable submitted version.
 */

type SnapshotSection = EmployerApplicantCvSnapshot["sections"][number];

/** A CV snapshot that may carry the snapshot-vs-live marker (talent flow). */
export type ApplicantCvPaperDocument = EmployerApplicantCvSnapshot & {
  snapshot?: boolean;
};

/**
 * Structural subset of `EmployerApplicantDetail` the paper reads, so talent
 * submissions (no job, partial profile) can feed the same component.
 */
export interface ApplicantCvPaperSubject {
  candidate: {
    name: string;
    title: string | null;
    location: string | null;
    email?: string | null;
    contactEmail?: string | null;
    phone?: string | null;
  };
  job?: { title: string } | null;
  candidateProfile?: {
    about?: string | null;
    bio?: string | null;
  } | null;
  coverLetter?: string | null;
}

export function formatAbsoluteDate(value: string, locale: string) {
  const date = new Date(value);

  if (Number.isNaN(date.getTime())) {
    return value;
  }

  return date.toLocaleDateString(locale, {
    month: "short",
    day: "numeric",
    year: "numeric",
  });
}

export function formatDateRange(
  startDate: string | null | undefined,
  endDate: string | null | undefined,
  locale: string,
) {
  if (!startDate && !endDate) {
    return null;
  }

  const start = startDate ? formatAbsoluteDate(startDate, locale) : null;
  const end = endDate ? formatAbsoluteDate(endDate, locale) : null;

  if (start && end) {
    return `${start} - ${end}`;
  }

  return start || end;
}

export function getApplicantCvOptions(detail: EmployerApplicantDetail | null) {
  if (!detail) {
    return [] as EmployerApplicantCvSnapshot[];
  }

  return [
    ...(detail.submittedCvDocument ? [detail.submittedCvDocument] : []),
    ...detail.additionalCvDocuments,
  ];
}

function getSnapshotSection(
  cv: ApplicantCvPaperDocument | null,
  type: SnapshotSection["type"],
) {
  return cv?.sections.find((section) => section.type === type) ?? null;
}

function getSnapshotMeta(
  item: SnapshotSection["items"][number],
  locale: string,
) {
  return [item.subtitle, formatDateRange(item.startDate, item.endDate, locale)]
    .filter(Boolean)
    .join(" · ");
}

function StatusBadge({ label }: { label: string }) {
  return <span className="tabbio-menu-badge">{label}</span>;
}

function CvSectionBlock({
  section,
  locale,
  isRTL,
}: {
  section: SnapshotSection;
  locale: string;
  isRTL: boolean;
}) {
  return (
    <section style={{ display: "grid", gap: 12 }}>
      <div
        style={{
          display: "flex",
          alignItems: "center",
          gap: 8,
          flexDirection: isRTL ? "row-reverse" : "row",
        }}
      >
        <div
          style={{
            fontSize: 15,
            fontWeight: 500,
            color: "var(--text-primary)",
          }}
        >
          {section.title}
        </div>
      </div>

      <div style={{ display: "grid", gap: 14 }}>
        {section.items.map((item) => (
          <div
            key={item.id}
            style={{
              display: "grid",
              gap: 6,
              textAlign: "start",
            }}
          >
            <div
              style={{
                fontSize: 14,
                fontWeight: 600,
                lineHeight: "20px",
                color: "var(--text-primary)",
              }}
            >
              {item.title}
            </div>
            {getSnapshotMeta(item, locale) ? (
              <div
                style={{
                  fontSize: 12,
                  lineHeight: "18px",
                  color: "var(--text-tertiary)",
                }}
              >
                {getSnapshotMeta(item, locale)}
              </div>
            ) : null}
            {item.description ? (
              <div
                style={{
                  fontSize: 13,
                  lineHeight: "20px",
                  color: "var(--text-secondary)",
                  whiteSpace: "pre-wrap",
                }}
              >
                {item.description}
              </div>
            ) : null}
            {item.tags?.length ? (
              <div
                style={{
                  display: "flex",
                  gap: 6,
                  flexWrap: "wrap",
                  marginTop: 2,
                }}
              >
                {item.tags.map((tag) => (
                  <span key={`${item.id}:${tag}`} className="tabbio-menu-badge">
                    {tag}
                  </span>
                ))}
              </div>
            ) : null}
          </div>
        ))}
      </div>
    </section>
  );
}

export function EmployerApplicantCvPaper({
  detail,
  cv,
  locale,
  isRTL,
  hasAttachedCv = true,
}: {
  detail: ApplicantCvPaperSubject;
  cv: ApplicantCvPaperDocument | null;
  locale: string;
  isRTL: boolean;
  /**
   * Whether the application carries its own submitted CV document. False only
   * when the API returned no `submittedCvDocument`, in which case whatever is
   * displayed is the candidate's live CV standing in for missing evidence.
   */
  hasAttachedCv?: boolean;
}) {
  const { t } = useTranslation(["common", "employer"]);
  const summary =
    cv?.summary ||
    detail.candidateProfile?.about ||
    detail.candidateProfile?.bio ||
    detail.coverLetter ||
    null;
  const orderedSections = [
    getSnapshotSection(cv, "experience"),
    getSnapshotSection(cv, "education"),
    getSnapshotSection(cv, "skills"),
    getSnapshotSection(cv, "certifications"),
    getSnapshotSection(cv, "projects"),
    getSnapshotSection(cv, "languages"),
    ...(cv ? cv.sections.filter((section) => section.type === "custom") : []),
  ].filter(Boolean) as SnapshotSection[];

  // §5.6: the document is clearly labeled. `snapshot === false` alone does NOT
  // mean "nothing was attached": the API stamps it on every additional
  // (non-submitted) CV too, so testing it on its own labelled the candidate's
  // other CVs "Current CV" and claimed nothing was attached to an application
  // that plainly has an attached snapshot. A document is a live-CV stand-in
  // only when it is the submitted slot's fallback, or when the application has
  // no submitted document at all.
  const isLiveSubmittedFallback = Boolean(
    cv && cv.snapshot === false && (cv.isSubmitted || !hasAttachedCv),
  );
  // Two different facts, and the employer must not be told the wrong one.
  // `isSubmitted` here means the API DID find an attached CV for this
  // application and only fell back to the live row because no snapshot was
  // stored at submission time (the pre-evidence rows). Claiming "no CV was
  // attached" for those is false — that sentence belongs to the case where
  // there is no submitted document at all.
  const liveCvNoticeKey = cv?.isSubmitted
    ? "employer:applications.unsavedSnapshotNoticeBody"
    : "employer:applications.currentCvNoticeBody";
  const paperLabel = !cv
    ? null
    : isLiveSubmittedFallback
      ? t("employer:applications.currentCvNotice")
      : cv.isSubmitted
        ? t("employer:applications.submittedCv")
        : t("common:jobApplicants.cv.additional");
  const paperTime = !cv
    ? null
    : cv.isSubmitted && !isLiveSubmittedFallback
      ? t("employer:applications.submittedCvAt", {
          date: formatAbsoluteDate(cv.updatedAt, locale),
        })
      : t("common:jobApplicants.cv.updatedOn", {
          date: formatAbsoluteDate(cv.updatedAt, locale),
        });

  return (
    <div
      style={{
        borderRadius: 24,
        backgroundColor: "rgba(0,0,0,0.04)",
        padding: 24,
        display: "grid",
        gap: 22,
      }}
    >
      <div
        style={{
          display: "grid",
          gap: 6,
          paddingBottom: 16,
          borderBottom: "1px solid var(--gem-outline-variant)",
          textAlign: "start",
        }}
      >
        <div
          style={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            gap: 12,
            flexDirection: isRTL ? "row-reverse" : "row",
          }}
        >
          <div
            style={{
              fontSize: 22,
              lineHeight: "28px",
              fontWeight: 500,
              color: "var(--text-primary)",
            }}
            dir="auto"
          >
            {detail.candidate.name}
          </div>
          {paperLabel ? <StatusBadge label={paperLabel} /> : null}
        </div>
        {isLiveSubmittedFallback ? (
          <div
            style={{
              fontSize: 13,
              lineHeight: "19px",
              color: "var(--text-tertiary)",
            }}
          >
            {t(liveCvNoticeKey)}
          </div>
        ) : null}
        <div
          style={{
            fontSize: 14,
            lineHeight: "20px",
            color: "var(--text-secondary)",
          }}
          dir="auto"
        >
          {detail.candidate.title || detail.job?.title || ""}
        </div>
        <div
          style={{
            fontSize: 13,
            lineHeight: "19px",
            color: "var(--text-tertiary)",
          }}
        >
          {/* The phone gets its own explicit ltr run: "+971…" starts with a
              neutral character, so it would otherwise inherit the Arabic
              paragraph direction and paint as "971500000001+". */}
          {[detail.candidate.location, detail.candidate.contactEmail || detail.candidate.email]
            .filter(Boolean)
            .join(" · ")}
          {detail.candidate.phone ? (
            <>
              {detail.candidate.location ||
              detail.candidate.contactEmail ||
              detail.candidate.email
                ? " · "
                : null}
              <span dir="ltr">{detail.candidate.phone}</span>
            </>
          ) : null}
        </div>
        {paperTime ? (
          <div
            style={{
              fontSize: 12,
              lineHeight: "18px",
              color: "var(--text-tertiary)",
            }}
          >
            {paperTime}
          </div>
        ) : null}
      </div>

      {summary ? (
        <div
          style={{
            fontSize: 14,
            lineHeight: "22px",
            color: "var(--text-secondary)",
            textAlign: "start",
            whiteSpace: "pre-wrap",
          }}
          dir="auto"
        >
          {summary}
        </div>
      ) : null}

      {orderedSections.length ? (
        orderedSections.map((section) => (
          <CvSectionBlock
            key={section.id}
            section={section}
            locale={locale}
            isRTL={isRTL}
          />
        ))
      ) : summary ? null : (
        <div
          style={{
            fontSize: 14,
            lineHeight: "21px",
            color: "var(--text-secondary)",
            textAlign: "center",
          }}
        >
          {/* "Not attached" and "attached but empty" are different facts. This
              paper only renders at all when a document exists, so a document
              with no sections must NOT claim nothing was attached. */}
          {cv
            ? t("employer:applications.cvNoSections")
            : t("common:jobApplicants.noData.cv")}
        </div>
      )}
    </div>
  );
}
